/* eslint-disable no-undef */
import { MsAuth } from '@api/ms-auth';
import { MsB2b } from '@api/ms-b2b';
import { MsAccount } from '@api/ms-account';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { isWebPlatform, isWeb, isNative } from '@global/screens';
import { removeAllQueues } from '@global/background';
import {
  NET_DOMAIN,
  VERSION,
  WEB_SOURCE,
  COOKIES_DOMAIN,
  COOKIES_EXPIRY,
} from '@constants';
import {
  saveDataToStorage,
  clearStorageData,
  getEnvironment,
  isVn,
} from '@global/helpers';
import Cookies from 'js-cookie';
import SyncStorage from '@global/storage';

import packageJson from '../../../../package.json';

export const AUTHENTICATE = 'AUTHENTICATE';
export const USER_ME = 'USER_ME';
export const USER_DATA = 'USER_DATA';
export const USER_MENU = 'USER_MENU';
export const TOTAL_NOTIFICATION = 'TOTAL_NOTIFICATION';

import store from '@/store';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';

const Config = getEnvironment();

const currentDomain = isWebPlatform ? window.location.hostname : '';
const storage = isWeb ? localStorage : AsyncStorage;

export const authenticate = (payload) => {
  return { type: AUTHENTICATE, data: payload };
};

export const handleUserMe = (payload) => {
  return { type: USER_ME, data: payload };
};

export const handleUserData = (payload) => {
  return { type: USER_DATA, data: payload };
};

export const handleUserMenu = (payload) => {
  return { type: USER_MENU, data: payload };
};

export const handleNotification = (payload) => {
  return { type: TOTAL_NOTIFICATION, data: payload };
};

export const userMe = () => {
  return async (dispatch) => {
    const removeUserLogin = async () => {
      // Set cookies country to current url
      if (isWebPlatform) {
        const coun = isVn() ? 'VN' : 'ID';
        Cookies.set('soc_cn', coun, {
          domain: COOKIES_DOMAIN,
          expires: COOKIES_EXPIRY,
        });
      } else {
        SyncStorage.remove('soc_cn');
      }

      dispatch(
        authenticate({
          token: '',
          userId: '',
          user: {},
        }),
      );
      await clearStorageData('B2B-SOCIOLLA-USER');
      await clearStorageData('B2B-SOCIOLLA-TOKEN');
    };
    if (!store.getState().auth.userMeFetched) {
      dispatch(handleUserMe(true));
      try {
        const res = await MsAuth.userMe();
        if (res.status == 200) {
          const data = res.data.data;

          let selectedCountry = isVn() ? 'VN' : 'ID';

          // Prevent user ID access VN and vice versa
          const userIsActiveFor = data.is_active_for || ['b2b'];
          if (!WEB_SOURCE.some((source) => userIsActiveFor.includes(source))) {
            await removeUserLogin();
            return false;
          }
          if (isWebPlatform) {
            if (!userIsActiveFor.includes(isVn() ? 'b2b_vn' : 'b2b')) {
              await removeUserLogin();
              return false;
            }
          } else {
            if (userIsActiveFor.length == 1) {
              selectedCountry = userIsActiveFor[0] == 'b2b' ? 'ID' : 'VN';
            }
          }
          await setCountryCookieAndRedirect(selectedCountry, dispatch);

          const payload = {
            userId: data._id,
            token: data.clients.dashboard.token,
            user: data,
          };
          const res_menu = await MsAuth.userMenu();
          if (
            res_menu.status == 200 &&
            res_menu.data &&
            res_menu.data.data &&
            res_menu.data.data.length
          ) {
            dispatch(handleUserMenu(res_menu.data.data));
          }
          dispatch(authenticate(payload));
          await saveDataToStorage('B2B-SOCIOLLA-USER', payload);
          await saveDataToStorage('B2B-SOCIOLLA-TOKEN', payload.token);
          const notif = await MsB2b.getNotificationsCount();
          if (notif?.data?.data) {
            dispatch(handleNotification(notif.data.data));
          }
          return res.data.data;
        } else {
          await removeUserLogin();
          return false;
        }
      } catch (err) {
        await removeUserLogin();
        return false;
      }
    }
  };
};

export const login = (payload) => {
  return async (dispatch) => {
    try {
      dispatch(handleUserMe(false));
      const res = await MsAuth.login(payload);
      const data = res.data.data;

      if (!NET_DOMAIN.includes(currentDomain)) {
        if (data.role == '3pl') {
          // If auth user is 3pl and internal domain, redirect to net domain
          window.location.href = `https://${
            currentDomain.includes('uat') || currentDomain.includes('localhost')
              ? NET_DOMAIN[1]
              : NET_DOMAIN[0]
          }`;
          return true;
        }
      }

      const saveData = { token: data.token, userId: data._id, user: data };
      dispatch(authenticate(saveData));
      dispatch(handleUserData(data));
      await saveDataToStorage('B2B-SOCIOLLA-USER', saveData);
      await saveDataToStorage('B2B-SOCIOLLA-TOKEN', saveData.token);
      await dispatch(userMe());
      return data;
    } catch (err) {
      return err.response.data;
    }
  };
};

export const forgetPassword = (payload) => {
  return async () => {
    try {
      const res = await MsAuth.forgetPassword(payload);
      const data = res.data.success;
      return data;
    } catch (err) {
      return err.response.data.success;
    }
  };
};

export const resetPassword = (payload) => {
  return async () => {
    try {
      const res = await MsAuth.resetPassword(payload);
      const data = res.data;
      return data;
    } catch (err) {
      return err.response.data;
    }
  };
};

export const resetVersion = () => {
  storage.removeItem('last_version');
};

const handleVersionUpdate = async (storage, last_version) => {
  const response = await MsAccount.updateVersion({ last_version });

  if (response.data) {
    await storage.setItem('last_version', last_version);
  }
};

export const updateAppVersion = () => {
  return async (dispatch) => {
    try {
      const last_version = isWeb ? packageJson.version : VERSION;

      await handleVersionUpdate(storage, last_version);
    } catch (err) {
      dispatch(
        showAlert({
          show: true,
          isModal: true,
          title: 'Error',
          description: 'Error updating version',
          isConfirm: false,
        }),
      );
    }
  };
};

export const setCountryCookieAndRedirect = async (
  country,
  dispatch = () => {},
) => {
  if (isNative) {
    const currentCountry = SyncStorage.get('soc_cn');

    if (currentCountry === country) {
      return;
    } else {
      SyncStorage.set('soc_cn', country);
      if (global.pageBase?.reset) {
        const res_menu = await MsAuth.userMenu();
        if (
          res_menu.status === 200 &&
          res_menu.data &&
          res_menu.data.data &&
          res_menu.data.data.length
        ) {
          dispatch(handleUserMenu(res_menu.data.data));
        }

        global.pageBase.reset({
          index: 0,
          routes: [
            {
              name: 'HomeNavigator',
              params: { screen: 'HomeScreen' },
            },
          ],
        });
      }

      return;
    }
  } else {
    const countryCookie = Cookies.get('soc_cn');
    if (!Config.B2B_WEB_VN_URL || !Config.B2B_WEB_URL) {
      console.log('Missing ID and VN URL');
      return;
    }

    const isLocalhost = window.location.hostname === 'localhost';
    let targetUrl = '';

    if (!isLocalhost) {
      targetUrl = country === 'ID' ? Config.B2B_WEB_URL : Config.B2B_WEB_VN_URL;
    }

    Cookies.set('soc_cn', country, {
      domain: COOKIES_DOMAIN,
      expires: COOKIES_EXPIRY,
    });

    let targetHostname = '';

    if (!isLocalhost) {
      targetHostname = new URL(targetUrl).hostname;
    }

    if (NET_DOMAIN.includes(currentDomain)) {
      // Handle external and set to ID
      Cookies.set('soc_cn', 'ID', {
        domain: COOKIES_DOMAIN,
        expires: COOKIES_EXPIRY,
      });

      return;
    }

    if (
      targetUrl &&
      !isLocalhost &&
      window.location.hostname !== targetHostname
    ) {
      window.location.replace(targetUrl);
    } else if (isLocalhost && countryCookie !== country) {
      window.location.reload();
    }
  }
};

export const logout = () => {
  return async (dispatch) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      resetVersion();
      await MsAuth.logout();
      await clearStorageData('B2B-SOCIOLLA-USER');
      await clearStorageData('B2B-SOCIOLLA-TOKEN');
      SyncStorage.remove('soc_cn');
      dispatch(authenticate({ token: null, userId: '', user: {} }));
      dispatch(handleUserData({}));
      removeAllQueues();
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (err) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };
};
